/**
 * کارت امتیاز ابعادی.
 *
 * ⚠️ **دلیل هر ردیف همیشه در دسترس است، نه پشت یک کلیک اختیاری.** عددِ بی‌دلیل
 * دقتِ کاذب می‌سازد: خواننده به «۴ در برابر ۲» اعتماد می‌کند بدون اینکه بداند
 * مبنایش چیست. نوار امتیاز نشانه است و معنا را متن حمل می‌کند.
 *
 * جمع وزنی عمداً **نمایش داده نمی‌شود**. یک عدد نهایی، مقایسه را به یک مسابقه‌ی
 * حساب تبدیل می‌کرد و خواننده دیگر ابعاد را نمی‌خواند؛ در حالی که تصمیم واقعی
 * روی یکی دو بُعد وزن‌دار گرفته می‌شود، نه روی میانگین.
 */

import { fa } from '@/lib/format'

import type { ScorecardRow } from '@/lib/types'

interface DimensionalScorecardProps {
  rows: ScorecardRow[]
}

export const DimensionalScorecard = ({ rows }: DimensionalScorecardProps) => {
  if (rows.length === 0) {
    return (
      <p className="p-5 text-sm leading-relaxed text-ink-muted">
        بُعد قابل مقایسه‌ای ثبت نشد.
      </p>
    )
  }

  return (
    <div className="divide-y divide-line">
      {rows.map((row) => {
        const leader =
          row.option_a_score === row.option_b_score
            ? 'برابر'
            : row.option_a_score > row.option_b_score
              ? 'الف'
              : 'ب'
        return (
          <div key={row.dimension} className="space-y-2.5 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-semibold text-ink">{row.dimension}</p>
              <p className="text-xs text-ink-faint">
                وزن {fa(row.weight)} از ۵ · برتری: {leader}
              </p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <ScoreBar label="تصمیم الف" score={row.option_a_score} />
              <ScoreBar label="تصمیم ب" score={row.option_b_score} />
            </div>
            <p className="text-sm leading-relaxed text-ink-muted">{row.explanation}</p>
          </div>
        )
      })}
    </div>
  )
}

/** نوار امتیاز یک گزینه. عدد همیشه نوشته می‌شود؛ نوار فقط نشانه است. */
const ScoreBar = ({ label, score }: { label: string; score: number }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between gap-2 text-xs text-ink-muted">
      <span>{label}</span>
      <span className="tabular-nums">{fa(score)} از ۵</span>
    </div>
    <div
      className="h-1.5 overflow-hidden rounded-full bg-surface-sunken"
      role="img"
      aria-label={`${label}: ${String(score)} از ۵`}
    >
      <div
        className="h-full rounded-full bg-accent"
        style={{ inlineSize: `${String((score / 5) * 100)}%` }}
      />
    </div>
  </div>
)
